import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { Button } from './ui/button';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from './ui/navigation-menu';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import DemoModal from './DemoModal';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Smart nav click: smooth-scroll to hero (top) if on same page,
  // otherwise navigate to target page and land at the top (hero section).
  const handleNavClick = (e, href, afterNav) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (location.pathname === href) {
      // Already on this page → smooth scroll to hero (top)
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Different page → navigate and land at top (hero) automatically
      navigate(href);
      // Reset scroll position right after route change so hero is visible
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      });
    }
    if (typeof afterNav === 'function') afterNav();
  };

  // Realistic mouse click sound effect on logo hover
  const playHoverSound = () => {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    const filter = audioContext.createBiquadFilter();
    
    // Create a sharp, realistic click sound
    oscillator.type = 'square';
    oscillator.frequency.setValueAtTime(2000, audioContext.currentTime);
    
    // Quick attack and decay for click effect
    gainNode.gain.setValueAtTime(0.06, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.08);
    
    // High-pass filter for crisp click
    filter.type = 'highpass';
    filter.frequency.setValueAtTime(1000, audioContext.currentTime);
    
    oscillator.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.08);
  };

  const productLinks = [
    { title: 'Broadcast Ready Commercials', href: '/product/broadcast-commercials', desc: 'Big-Screen Ads for Growing Businesses' },
    { title: 'AI Concierge', href: '/product/ai-concierge', desc: 'Your AI-native Voice Agent' },
    { title: 'AI App Builder', href: '/product/ai-app-builder', desc: 'Get Apps Without the Developer Bill' },
    { title: 'AI Employee', href: '/products/ai-employee', desc: 'Your Always-On AI Teammate' },
  ];

  const solutionLinks = [
    { title: 'Customer Service', href: '/solutions/customer-service', desc: 'AI-powered support' },
    { title: 'Sales', href: '/solutions/sales', desc: 'Intelligent sales automation' },
    { title: 'Finance', href: '/solutions/finance', desc: 'Smart financial operations' },
    { title: 'Human Resources', href: '/solutions/human-resources', desc: 'HR automation & insights' },
    { title: 'Operations', href: '/solutions/operations', desc: 'Streamlined operations' },
  ];

  const DropdownContent = ({ links }) => (
    <ul className="grid w-[400px] gap-3 p-4 bg-white">
      {links.map((link) => (
        <li key={link.href}>
          <Link
            to={link.href}
            onClick={(e) => handleNavClick(e, link.href)}
            className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-[#FDF7F9] focus:bg-[#FDF7F9] group"
            data-testid={`nav-link-${link.href.split('/').pop()}`}
          >
            <div className="text-sm font-medium leading-none text-black/90 group-hover:text-[#E8A0B8]">
              {link.title}
            </div>
            <p className="line-clamp-2 text-sm leading-snug text-black/60">
              {link.desc}
            </p>
          </Link>
        </li>
      ))}
    </ul>
  );

  return (
    <>
      <nav className="sticky top-0 z-50 w-full border-b border-black/10 bg-white/95 backdrop-blur-xl" data-testid="top-navigation">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <Link to="/" onClick={(e) => handleNavClick(e, '/')} className="flex items-center space-x-3 group min-w-0 flex-shrink">
              {/* Logo Image - Square ratio, blended styling with hover zoom */}
              <img 
                src="https://customer-assets.emergentagent.com/job_googlyn-ai/artifacts/yba1j97l_P3.png" 
                alt="GoogLyn AI Logo" 
                className="h-6 w-6 object-cover rounded-sm opacity-90 hover:scale-[4] transition-transform duration-500 ease-out cursor-pointer relative z-[100] flex-shrink-0"
                style={{ aspectRatio: '1/1', transformOrigin: 'top left' }}
                onMouseEnter={playHoverSound}
              />
              {/* Brand Name - Original Styling Restored */}
              <div className="text-lg sm:text-xl font-medium text-black/90 group-hover:text-[#E8A0B8] transition-colors whitespace-nowrap">
                GoogLyn AI
              </div>
              <span className="hidden lg:inline text-xs text-black/50 whitespace-nowrap">Elegant Intelligence</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex md:items-center md:space-x-1">
              <NavigationMenu>
                <NavigationMenuList>
                  <NavigationMenuItem>
                    <NavigationMenuTrigger className="bg-transparent text-black/80 hover:text-[#E8A0B8] hover:bg-[#FDF7F9]" data-testid="nav-product-menu-trigger">
                      Products
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <DropdownContent links={productLinks} />
                    </NavigationMenuContent>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <Link
                      to="/product/enterprise-solutions"
                      onClick={(e) => handleNavClick(e, '/product/enterprise-solutions')}
                      className="inline-flex items-center justify-center px-4 py-2 text-sm text-black/80 hover:text-[#E8A0B8] hover:bg-[#FDF7F9] rounded-md transition-colors"
                      data-testid="nav-enterprise-solutions-link"
                    >
                      Enterprise Solutions
                    </Link>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <Link 
                      to="/about"
                      onClick={(e) => handleNavClick(e, '/about')}
                      className="inline-flex items-center justify-center px-4 py-2 text-sm text-black/80 hover:text-[#E8A0B8] hover:bg-[#FDF7F9] rounded-md transition-colors"
                      data-testid="nav-about-link"
                    >
                      About
                    </Link>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <Link 
                      to="/careers"
                      onClick={(e) => handleNavClick(e, '/careers')}
                      className="inline-flex items-center justify-center px-4 py-2 text-sm text-black/80 hover:text-[#E8A0B8] hover:bg-[#FDF7F9] rounded-md transition-colors"
                      data-testid="nav-careers-link"
                    >
                      Careers
                    </Link>
                  </NavigationMenuItem>
                </NavigationMenuList>
              </NavigationMenu>
            </div>

            {/* Right side */}
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => setDemoOpen(true)}
                className="hidden md:inline-flex bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-105"
                data-testid="nav-book-demo-button"
              >
                Book a demo
              </Button>

              {/* Mobile menu button */}
              <Sheet open={isOpen} onOpenChange={setIsOpen}>
                <SheetTrigger asChild className="md:hidden">
                  <Button variant="ghost" size="icon" data-testid="nav-mobile-menu-button">
                    <Menu className="h-6 w-6 text-black" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[300px] bg-white border-black/10">
                  <div className="flex flex-col space-y-4 mt-8">
                    <Accordion type="single" collapsible className="w-full">
                      <AccordionItem value="product" className="border-black/10">
                        <AccordionTrigger className="text-black/80 hover:text-black">Products</AccordionTrigger>
                        <AccordionContent>
                          <div className="flex flex-col space-y-2 pl-4">
                            {productLinks.map((link) => (
                              <Link
                                key={link.href}
                                to={link.href}
                                onClick={(e) => handleNavClick(e, link.href, () => setIsOpen(false))}
                                className="text-sm text-black/70 hover:text-black"
                              >
                                {link.title}
                              </Link>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>

                    <Link
                      to="/product/enterprise-solutions"
                      onClick={(e) => handleNavClick(e, '/product/enterprise-solutions', () => setIsOpen(false))}
                      className="py-4 text-black/80 hover:text-black border-b border-black/10 text-sm font-medium"
                      data-testid="nav-mobile-enterprise-solutions-link"
                    >
                      Enterprise Solutions
                    </Link>

                    <Link
                      to="/about"
                      onClick={(e) => handleNavClick(e, '/about', () => setIsOpen(false))}
                      className="px-4 py-2 text-black/80 hover:text-black"
                      data-testid="nav-mobile-about-link"
                    >
                      About
                    </Link>

                    <Link
                      to="/careers"
                      onClick={(e) => handleNavClick(e, '/careers', () => setIsOpen(false))}
                      className="px-4 py-2 text-black/80 hover:text-black"
                      data-testid="nav-mobile-careers-link"
                    >
                      Careers
                    </Link>

                    <Button
                      onClick={() => {
                        setIsOpen(false);
                        setDemoOpen(true);
                      }}
                      className="w-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]"
                    >
                      Book a demo
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </nav>

      <DemoModal open={demoOpen} onOpenChange={setDemoOpen} />
    </>
  );
};

export default Navigation;
